import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["pageTitle", "pageSlogan", "perfumesGrid", "pagination", "genderFilter", "classFilter", "priceFilter"];

  allRawPerfumes = [];
  filteredAndSortedPerfumes = [];
  currentPage = 1;
  perfumesPerPage = 12;

  currentGenderFilter = 'all_genders';
  currentClassFilter = 'all_classes';
  currentPriceSort = 'none';

  connect() {
    console.log("All Perfumes controller connected!");
    this.fetchPerfumes();
  }

  async fetchPerfumes() {
    const apiUrl = `/api/v1/parfums`;

    try {
      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      this.allRawPerfumes = data.map(perfume => {
        const validVariants = perfume.variants && perfume.variants.length > 0 
          ? perfume.variants.filter(v => v.price && parseFloat(v.price) > 0)
          : [{ size: 'N/A', price: 0 }];
        return {
          ...perfume,
          min_price: validVariants.length > 0 ? Math.min(...validVariants.map(v => parseFloat(v.price))) : 0
        };
      });
      this.applyFiltersAndSortAndRender();
    } catch (error) {
      console.error("Error fetching perfumes:", error);
      this.perfumesGridTarget.innerHTML = "<p class='error-message'>Impossible de charger les parfums. Veuillez réessayer plus tard.</p>";
      this.pageTitleTarget.textContent = "Erreur de chargement";
      this.pageSloganTarget.textContent = "Une erreur est survenue lors du chargement des parfums.";
    }
  }

  filterPerfumes(event) {
    if (event.currentTarget === this.genderFilterTarget) {
      this.currentGenderFilter = event.target.value;
    } else if (event.currentTarget === this.classFilterTarget) {
      this.currentClassFilter = event.target.value;
    } else if (event.currentTarget === this.priceFilterTarget) {
      this.currentPriceSort = event.target.value;
    }
    this.applyFiltersAndSortAndRender();
  }

  applyFiltersAndSortAndRender() {
    console.log("--- applyFiltersAndSortAndRender called for All Perfumes ---");
    let tempPerfumes = [...this.allRawPerfumes];

    if (this.currentGenderFilter !== 'all_genders') {
      tempPerfumes = tempPerfumes.filter(perfume => {
        const perfumeCategoryLower = perfume.category ? perfume.category.toLowerCase() : '';
        return perfumeCategoryLower === this.currentGenderFilter;
      });
    }
    console.log("Perfumes after gender filter:", tempPerfumes.length);

    if (this.currentClassFilter !== 'all_classes') {
      tempPerfumes = tempPerfumes.filter(perfume => {
        const perfumeFragranceClassLower = perfume.fragrance_class ? perfume.fragrance_class.toLowerCase() : '';
        return perfumeFragranceClassLower === this.currentClassFilter;
      });
    }
    console.log("Perfumes after class filter:", tempPerfumes.length);

    if (this.currentPriceSort === 'asc') {
      tempPerfumes.sort((a, b) => a.min_price - b.min_price);
    } else if (this.currentPriceSort === 'desc') {
      tempPerfumes.sort((a, b) => b.min_price - a.min_price);
    }
    console.log("Perfumes after price sort:", tempPerfumes.length);

    this.filteredAndSortedPerfumes = tempPerfumes;
    this.currentPage = 1;

    if (this.filteredAndSortedPerfumes.length === 0) {
      this.perfumesGridTarget.innerHTML = "<p class='no-perfumes-message'>Aucun parfum trouvé pour cette sélection pour le moment.</p>";
      this.paginationTarget.innerHTML = '';
      return;
    }

    this.renderPerfumes();
    this.renderPagination();
    console.log("--- Rendering complete for All Perfumes ---");
  }

  renderPerfumes() {
    const startIndex = (this.currentPage - 1) * this.perfumesPerPage;
    const endIndex = startIndex + this.perfumesPerPage;
    const perfumesToDisplay = this.filteredAndSortedPerfumes.slice(startIndex, endIndex);

    const perfumeHtml = perfumesToDisplay.map(perfume => {
      const imageUrl = perfume.image_url || "/placeholder.svg?height=250&width=250&text=Parfum";
      const availabilityStatus = perfume.disponible ? 'Disponible' : 'Rupture de stock';
      const availabilityClass = perfume.disponible ? 'perfume-availability-badge--available' : 'perfume-availability-badge--unavailable';
      const validVariants = perfume.variants && perfume.variants.length > 0 
        ? perfume.variants.filter(v => v.price && parseFloat(v.price) > 0)
        : [{ size: 'N/A', price: 0 }];
      const defaultVariant = validVariants.length > 0 ? validVariants[0] : { size: 'N/A', price: 0 };
      const safePrice = parseFloat(defaultVariant.price) || 0;
      const safeSize = defaultVariant.size || 'N/A';

      return `
        <div class="perfume-card">
          <a href="/vitrine/parfums/${perfume.id}" class="perfume-card-link">
            <div class="perfume-image-wrapper">
              <img src="${imageUrl}" alt="${perfume.name}" class="perfume-image">
              <div class="image-overlay"></div>
            </div>
            <div class="perfume-content">
              <h3 class="perfume-name">${perfume.name}</h3>
              <p class="perfume-brand">${perfume.brand ? perfume.brand.name : 'Inconnue'}</p>
              <p class="perfume-description">${this.truncateText(perfume.description, 100)}</p>
              <div class="perfume-meta">
                <span class="perfume-category">${perfume.category ? this.capitalizeFirstLetter(perfume.category) : ''}</span>
                <span class="perfume-class">${perfume.fragrance_class ? this.capitalizeFirstLetter(perfume.fragrance_class) : ''}</span>
              </div>
              <p class="perfume-price">${this.formatCurrency(safePrice)}</p>
              <div class="perfume-volume">${safeSize}</div>
              <div class="perfume-card-actions">
                <button
                  class="perfume-card-add-to-cart-btn"
                  data-action="click->cart#addToCart"
                  data-parfum-id="${perfume.id}"
                  data-parfum-name="${perfume.name}"
                  data-parfum-price="${safePrice}"
                  data-parfum-size="${safeSize}"
                  data-parfum-image-url="${imageUrl}"
                  ${!perfume.disponible || validVariants.length === 0 || safePrice <= 0 ? 'disabled' : ''}
                >
                  Ajouter au panier
                </button>
              </div>
            </div>
          </a>
          <div class="perfume-availability-badge ${availabilityClass}">${availabilityStatus}</div>
        </div>
      `;
    }).join('');

    this.perfumesGridTarget.innerHTML = perfumeHtml;
  }

  renderPagination() {
    const totalPages = Math.ceil(this.filteredAndSortedPerfumes.length / this.perfumesPerPage);
    if (totalPages <= 1) {
      this.paginationTarget.innerHTML = '';
      return;
    }

    let paginationHtml = '<ul class="pagination">';
    
    // Bouton Précédent
    paginationHtml += `
      <li class="page-item ${this.currentPage === 1 ? 'disabled' : ''}">
        <a href="#" data-action="click->all-perfumes#previousPage" class="page-link">«</a>
      </li>
    `;

    // Générer les numéros de page intelligents
    const pageNumbers = this.generatePageNumbers(this.currentPage, totalPages);
    
    pageNumbers.forEach((pageNum, index) => {
      if (pageNum === '...') {
        paginationHtml += `<li class="page-item page-ellipsis"><span class="page-link">...</span></li>`;
      } else {
        paginationHtml += `
          <li class="page-item ${this.currentPage === pageNum ? 'current' : ''}">
            <a href="#" data-action="click->all-perfumes#goToPage" data-page="${pageNum}" class="page-link">${pageNum}</a>
          </li>
        `;
      }
    });

    // Bouton Suivant
    paginationHtml += `
      <li class="page-item ${this.currentPage === totalPages ? 'disabled' : ''}">
        <a href="#" data-action="click->all-perfumes#nextPage" class="page-link">»</a>
      </li>
    `;
    
    paginationHtml += '</ul>';
    this.paginationTarget.innerHTML = paginationHtml;
  }

  generatePageNumbers(currentPage, totalPages) {
    const pages = [];
    
    // Toujours afficher les 2 premières pages
    pages.push(1);
    if (totalPages > 1) pages.push(2);

    // Ajouter les pages autour de la page actuelle
    if (currentPage > 3) {
      pages.push('...');
    }
    
    if (currentPage > 2 && currentPage < totalPages - 1) {
      pages.push(currentPage - 1);
      pages.push(currentPage);
      pages.push(currentPage + 1);
    } else if (currentPage === totalPages && currentPage > 2) {
      pages.push(currentPage - 1);
      pages.push(currentPage);
    }

    // Ajouter les 2 dernières pages
    if (totalPages > 3 && currentPage < totalPages - 2) {
      pages.push('...');
    }
    
    if (totalPages > 2) pages.push(totalPages - 1);
    if (totalPages > 1) pages.push(totalPages);

    // Supprimer les doublons et trier
    return Array.from(new Set(pages.filter(p => p !== '...'))).sort((a, b) => a - b).reduce((acc, page, idx, arr) => {
      acc.push(page);
      // Ajouter les ellipses entre les écarts
      if (idx < arr.length - 1 && arr[idx + 1] - page > 1) {
        acc.push('...');
      }
      return acc;
    }, []);
  }

  previousPage(event) {
    event.preventDefault();
    if (this.currentPage > 1) {
      this.currentPage--;
      this.renderPerfumes();
      this.renderPagination();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  nextPage(event) {
    event.preventDefault();
    const totalPages = Math.ceil(this.filteredAndSortedPerfumes.length / this.perfumesPerPage);
    if (this.currentPage < totalPages) {
      this.currentPage++;
      this.renderPerfumes();
      this.renderPagination();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  goToPage(event) {
    event.preventDefault();
    const page = parseInt(event.currentTarget.dataset.page);
    if (page && page !== this.currentPage) {
      this.currentPage = page;
      this.renderPerfumes();
      this.renderPagination();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  formatCurrency(amount) {
    const num = parseFloat(amount) || 0;
    return new Intl.NumberFormat('fr-TN', { style: 'currency', currency: 'TND' }).format(num);
  }

  truncateText(text, maxLength) {
    if (!text || text.length <= maxLength) {
      return text || '';
    }
    return text.substring(0, maxLength) + '...';
  }

  capitalizeFirstLetter(string) {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  }
}